import { z } from 'zod';
// ---------------------------------------------------------------------------
// Podcast episode scripts (ENG-5100). The `langston-podcast` skill researches,
// writes and verifies a script; `publish_episode` voices it with ElevenLabs and
// publishes it to the company feed or the caller's personal feed. The shape is
// the skill's script JSON (references/scriptwriting.md).
// ---------------------------------------------------------------------------
export const PODCAST_FEEDS = ['company', 'personal'];
export const PODCAST_CADENCES = ['monthly', 'weekly'];
/** The skill's per-line ceiling; requests pack lines up to 2,000 characters. */
export const PODCAST_MAX_LINE_CHARS = 600;
/** Caps the credits one call can spend (~30 minutes of speech). */
export const PODCAST_MAX_SCRIPT_CHARS = 30_000;
/** A real calendar date: rejects 2026-02-30 and 2026-99-99, which Date would roll or refuse. */
function isCalendarDate(value) {
    if (!value || !/^\d{4}-\d{2}-\d{2}$/u.test(value))
        return false;
    const date = new Date(`${value}T00:00:00Z`);
    return !Number.isNaN(date.getTime()) && date.toISOString().slice(0, 10) === value;
}
export const PodcastLineSchema = z.object({
    speaker: z.string().min(1).describe('A key of `cast`.'),
    text: z.string().trim().min(1).max(PODCAST_MAX_LINE_CHARS),
    section: z
        .string()
        .trim()
        .min(1)
        .max(200)
        .optional()
        .describe('Set on the first line of each segment; becomes a timestamped chapter.'),
    sources: z.array(z.unknown()).optional().describe('Kept for the trace; not voiced.'),
});
/**
 * A next step the episode asks listeners to take (ENG-5153). Not voiced: the
 * spoken to-do list stays in `lines`; this is the same list in a form an agent
 * can act on, returned by `get_episode`.
 */
export const PodcastActionItemSchema = z.object({
    text: z.string().trim().min(1).max(500),
    owner: z
        .string()
        .trim()
        .min(1)
        .max(100)
        .optional()
        .describe('Who it falls to, when the episode says.'),
    refs: z
        .array(z.string().trim().min(1).max(300))
        .max(10)
        .optional()
        .describe('Linear issue ids (ENG-1234) or URLs the item rests on.'),
});
/** An ElevenLabs voice id, as `cast` and the narrator setting accept it. */
export const PodcastVoiceIdSchema = z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9]{8,40}$/u, 'a voice id is 8-40 letters and digits');
export const PodcastResourceSchema = z.object({
    title: z.string().trim().min(1).max(300),
    url: z.url({ protocol: /^https?$/u }),
});
export const PodcastScriptSchema = z
    .object({
    series: z
        .string()
        .trim()
        .min(1)
        .max(80)
        // The series is slugged into the episode identity; punctuation would collapse
        // distinct series ("C++", "C#") onto one id and replace each other's episodes.
        .regex(/^[A-Za-z0-9][A-Za-z0-9 '&-]*$/u, "series: letters, digits, spaces, hyphens, & and ' only")
        .describe('e.g. "Engineering Deep Dive".'),
    title: z
        .string()
        .trim()
        .min(1)
        .max(160)
        .describe('The episode name alone: no series prefix and no "(vN)"; both are added.'),
    version: z.number().int().min(1).max(99),
    window: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}\.\.\d{4}-\d{2}-\d{2}$/u, 'expected YYYY-MM-DD..YYYY-MM-DD'),
    cadence: z.enum(PODCAST_CADENCES).default('monthly'),
    feed: z.enum(PODCAST_FEEDS),
    summary: z.string().trim().min(1).max(4000),
    resources: z.array(PodcastResourceSchema).max(50).default([]),
    cast: z
        .record(z.string().min(1), z.object({ voice_id: PodcastVoiceIdSchema }))
        .refine((cast) => Object.keys(cast).length >= 1, 'cast needs at least one voice'),
    lines: z.array(PodcastLineSchema).min(1).max(500),
    action_items: z
        .array(PodcastActionItemSchema)
        .max(30)
        .default([])
        .describe('The episode’s next steps, structured for agents; not voiced.'),
})
    .superRefine((script, ctx) => {
    const [start, end] = script.window.split('..');
    if (!isCalendarDate(start) || !isCalendarDate(end) || end < start) {
        ctx.addIssue({
            code: 'custom',
            path: ['window'],
            message: 'window must be start..end, start <= end',
        });
    }
    const series = script.series.toLowerCase();
    if (script.title.toLowerCase().startsWith(`${series}:`)) {
        ctx.addIssue({
            code: 'custom',
            path: ['title'],
            message: 'title must not repeat the series prefix',
        });
    }
    if (/\(v\d+\)\s*$/iu.test(script.title)) {
        ctx.addIssue({
            code: 'custom',
            path: ['title'],
            message: 'title must not carry "(vN)"; set version',
        });
    }
    if (script.feed === 'personal' && Object.keys(script.cast).length !== 1) {
        ctx.addIssue({
            code: 'custom',
            path: ['cast'],
            message: 'a personal brief has one narrator',
        });
    }
    let total = 0;
    script.lines.forEach((line, i) => {
        total += line.text.length;
        if (!(line.speaker in script.cast)) {
            ctx.addIssue({
                code: 'custom',
                path: ['lines', i, 'speaker'],
                message: `no voice for speaker ${line.speaker}`,
            });
        }
    });
    if (total > PODCAST_MAX_SCRIPT_CHARS) {
        ctx.addIssue({
            code: 'custom',
            path: ['lines'],
            message: `script is ${total} characters; the limit is ${PODCAST_MAX_SCRIPT_CHARS}`,
        });
    }
});
// ---------------------------------------------------------------------------
// The personal brief's narrator (ENG-5141). People pick from a curated set of
// voices in Langston's ElevenLabs account, or paste the id of any other voice
// already in that account. Almanac never adds voices to the account.
// ---------------------------------------------------------------------------
/**
 * The voices offered in the narrator picker, in display order. Adding a voice
 * here is a reviewed code change; the voice must already be in Langston's
 * ElevenLabs account or the picker leaves it out.
 */
export const PODCAST_CURATED_VOICES = [
    { voice_id: 'TWutjvRaJqAX89preB4e', alias: 'Evan', company_feed: false },
    { voice_id: 'EST9Ui6982FZPSi7gCHi', alias: 'Elise', company_feed: false },
    { voice_id: 'kdnRe2koJdOK4Ovxn2DI', alias: 'Eryn', company_feed: true },
    { voice_id: 'ZoiZ8fuDWInAcwPXaVeq', alias: 'Josh', company_feed: true },
];
/** Narrates a brief when the person has chosen no voice. */
export const PODCAST_DEFAULT_VOICE = PODCAST_CURATED_VOICES[0];
/**
 * Where the narrator came from: the saved setting, the legacy `Weekly brief
 * preferences` guidance note, or the default.
 */
export const PODCAST_NARRATOR_SOURCES = ['setting', 'guidance_note', 'default'];
export const SetPodcastVoiceSchema = z.object({ voice_id: PodcastVoiceIdSchema });
/**
 * Why a voice id was not saved: `library_voice` is a public ElevenLabs library
 * voice that is not in Langston's account; `not_found` matches no voice.
 */
export const PODCAST_VOICE_REJECTIONS = ['library_voice', 'not_found'];
